import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type AppTheme = 'light' | 'dark' | 'ocean' | 'pearl' | 'forest' | 'sky';

export const THEMES: AppTheme[] = ['light', 'sky', 'dark', 'ocean', 'pearl', 'forest'];
const THEME_KEY = 'app_theme_v2';
const LEGACY_LANDING_THEME_KEY = 'mainLandingTheme';
const LANG_KEY = 'i18nextLng';

interface AppThemeContextValue {
  theme: AppTheme;
  setTheme: (t: AppTheme) => void;
  language: string;
  setLanguage: (lang: string) => void;
}

const AppThemeContext = createContext<AppThemeContextValue | undefined>(undefined);

const isTheme = (v: string | null): v is AppTheme =>
  v === 'light' || v === 'dark' || v === 'ocean' || v === 'pearl' || v === 'forest' || v === 'sky';

export const AppThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();

  const [theme, setThemeState] = useState<AppTheme>(() => {
    const saved = localStorage.getItem(THEME_KEY) || localStorage.getItem(LEGACY_LANDING_THEME_KEY);
    return isTheme(saved) ? saved : 'light';
  });

  const [language, setLanguageState] = useState<string>(() => {
    return localStorage.getItem(LANG_KEY) || 'al';
  });

  // Sync theme to localStorage
  const setTheme = useCallback((t: AppTheme) => {
    setThemeState(t);
    localStorage.setItem(THEME_KEY, t);
    localStorage.removeItem(LEGACY_LANDING_THEME_KEY);
  }, []);

  // Sync language to localStorage and i18n
  const setLanguage = useCallback(
    (lang: string) => {
      setLanguageState(lang);
      localStorage.setItem(LANG_KEY, lang);
      i18n.changeLanguage(lang);
    },
    [i18n],
  );

  // Initialize i18n language on mount
  useEffect(() => {
    const savedLang = localStorage.getItem(LANG_KEY);
    if (savedLang && savedLang !== i18n.language?.split('-')[0]) {
      i18n.changeLanguage(savedLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onLanguageChanged = (lang: string) => {
      const nextLang = lang.split('-')[0] || 'al';
      setLanguageState(nextLang);
      localStorage.setItem(LANG_KEY, nextLang);
    };

    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, [i18n]);

  return (
    <AppThemeContext.Provider value={{ theme, setTheme, language, setLanguage }}>
      {children}
    </AppThemeContext.Provider>
  );
};

export const useAppTheme = (): AppThemeContextValue => {
  const ctx = useContext(AppThemeContext);
  if (!ctx) {
    throw new Error('useAppTheme must be used within an AppThemeProvider');
  }
  return ctx;
};
